/**
 * useDemoNarration.ts
 *
 * The demo's caption board and its voice (card D16, plan §4.11). One module for
 * the whole app, in the shape `useDemoFeed`/`useDemoClock` established:
 *
 *  - **One listener set for the app.** `stores/socket.ts` attaches
 *    `attachDemoNarrationListeners` beside the clock's and the feeds' and
 *    detaches it with the socket it belongs to. A second `socket.on(
 *    'demo:narration')` anywhere else is a subscription that outlives the hub —
 *    and a cue arriving twice is a caption spoken twice.
 *  - **The queue is module state**, because a cue can arrive while no narration
 *    surface is mounted (the operator closed the FAB): the caption board must
 *    paint the line the run is on when it opens, not wait for the next beat.
 *  - **The audio element is ONE instance for the whole app.** Two cues never
 *    overlap — a new line stops the old one — and a pause, a seek, a mute or a
 *    rate pushed past the speech gate stops whatever is speaking. Vocabulary:
 *    `speaking` is the element's own state, never a guess.
 *
 * What this module deliberately does NOT own: which cue is due (the hub, D14),
 * the words (the script, D13), how long a caption holds or when a cue is spoken
 * (the pure rules in `utils/demoNarrationModel.ts`). Nesting the decision here
 * rather than in each surface is what keeps the settings page's "test line" and
 * the live board from disagreeing about the same cue.
 *
 * The preference pair (`enabled`, `volume`) lives in ONE localStorage key, read
 * totally at boot: a corrupt value means the defaults, never a broken player.
 */
import { computed, shallowRef, type ComputedRef } from 'vue';

import { getRuntimeConfig, toDirectHubApiUrl } from '../config/runtime';
import { onDemoFrameChange, serverFrame, type DemoClockFrame } from '../services/clockSync';
import { useLocaleStore } from '../stores/locale';
import type { HubSocket } from '../stores/socket';
import { tt } from '../utils/consoleText';
import {
  NARRATION_SPEAK_MAX_RATE,
  advanceQueue,
  captionFor,
  enqueueCue,
  holdMs,
  narrationAudioPath,
  narrationAudioUrl,
  narrationLanguage,
  readDemoNarrationCue,
  shouldSpeak,
  type DemoNarrationCue,
  type NarrationCaption,
  type NarrationQueue,
} from '../utils/demoNarrationModel';
import { useDemoClock } from './useDemoClock';

/** The ONE key the caption board's preferences live under. */
export const DEMO_NARRATION_STORAGE_KEY = 'otto-demo-narration';

/** What an operator may set: narration on/off, and how loud. */
export interface DemoNarrationPrefs {
  enabled: boolean;
  volume: number;
}

/** Narration is ON by default — a demo with silent captions is a bug, not a default. */
const DEFAULT_PREFS: DemoNarrationPrefs = { enabled: true, volume: 0.8 };

/** The caption being read (`null` between cues, and outside a demo). */
const visibleCue = shallowRef<DemoNarrationCue | null>(null);

/** The cue waiting for the current hold to expire. */
const pendingCue = shallowRef<DemoNarrationCue | null>(null);

/** The last cue the hub sent, kept for the settings page's "test line". */
let lastCue: DemoNarrationCue | null = null;

/** The operator's preference pair, persisted under one key. */
const prefs = shallowRef<DemoNarrationPrefs>(readPrefs());

/** The element is playing right now (its own events, never a guess). */
const speaking = shallowRef(false);

/** Why the browser refused to play (autoplay policy), ready to paint. */
const audioError = shallowRef<string | null>(null);

/** The hold timer for the visible cue. */
let holdTimer: ReturnType<typeof setTimeout> | null = null;

/** The app's one audio element; created on demand and kept. */
let audio: HTMLAudioElement | null = null;

/** The element has been played once inside a gesture (autoplay policy). */
let audioUnlocked = false;

/** A silent 8-sample WAV built at runtime — the gesture's own warm-up clip. */
let silentClip: string | null = null;

/** The cue whose clip the browser refused, so the next gesture can retry it. */
let blockedCue: DemoNarrationCue | null = null;

/** One pending retry (a gesture arrived; the refused line gets one more try). */
let retryTimer: ReturnType<typeof setTimeout> | null = null;

/** The console's own voice: a two-note CRT chirp, built in WebAudio (no asset). */
let blipContext: AudioContext | null = null;

/** The page-wide gesture listener has been installed (once per page). */
let gestureInstalled = false;

/**
 * A short terminal chirp — 880 Hz then 1320 Hz, ~80 ms each, scaled by the
 * narration volume. It marks a line arriving even when the clip itself cannot
 * play (a muted tab, a browser with no voices), and it is the operator's proof
 * that the narration surface is alive.
 */
export function playDemoNarrationBlip(volume = prefs.value.volume): void {
  try {
    if (typeof window === 'undefined') return;
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    if (!blipContext) blipContext = new Ctor();
    if (blipContext.state === 'suspended') void blipContext.resume().catch(() => {});
    const ctx = blipContext;
    const t0 = ctx.currentTime + 0.01;
    const gain = ctx.createGain();
    const level = Math.max(0, Math.min(1, volume)) * 0.12;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, level), t0 + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.2);
    gain.connect(ctx.destination);
    for (const [frequency, offset] of [
      [880, 0],
      [1320, 0.07],
    ] as const) {
      const oscillator = ctx.createOscillator();
      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(frequency, t0 + offset);
      oscillator.connect(gain);
      oscillator.start(t0 + offset);
      oscillator.stop(t0 + offset + 0.09);
    }
  } catch {
    // Sound is a nicety; a refused context must never break a caption.
  }
}

/**
 * The first pointer/key gesture anywhere unlocks narration.
 *
 * Chrome's autoplay policy grants a page audio only after a real gesture, and
 * the cue that arrives before one is refused (measured on the owner's browser:
 * captions typed, no voice, an amber "press Play once"). Rather than asking the
 * operator to find the Play button, the first click ANYWHERE warms the element
 * and replays the refused line — a demo starts with someone clicking.
 */
export function installDemoNarrationGestureUnlock(): void {
  if (gestureInstalled || typeof window === 'undefined') return;
  gestureInstalled = true;
  const unlockOnce = (): void => {
    window.removeEventListener('pointerdown', unlockOnce, true);
    window.removeEventListener('keydown', unlockOnce, true);
    unlockDemoNarration();
  };
  window.addEventListener('pointerdown', unlockOnce, true);
  window.addEventListener('keydown', unlockOnce, true);
}


/** Read the preference pair; anything unreadable means the defaults. */
function readPrefs(): DemoNarrationPrefs {
  try {
    if (typeof localStorage === 'undefined') return { ...DEFAULT_PREFS };
    const raw = localStorage.getItem(DEMO_NARRATION_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw) as Partial<DemoNarrationPrefs>;
    const volume = typeof parsed.volume === 'number' && Number.isFinite(parsed.volume)
      ? Math.min(1, Math.max(0, parsed.volume))
      : DEFAULT_PREFS.volume;
    return { enabled: parsed.enabled !== false, volume };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

/** Persist the pair. A storage failure (private mode, a full quota) is silent. */
function writePrefs(next: DemoNarrationPrefs): void {
  prefs.value = next;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(DEMO_NARRATION_STORAGE_KEY, JSON.stringify(next));
    }
  } catch {
    // The preference still applies to this session; only its memory is lost.
  }
}

/** The language a cue is read and spoken in, from the app's one locale store. */
function currentLanguage(): 'en' | 'sl' {
  try {
    return narrationLanguage(useLocaleStore().currentLocale);
  } catch {
    // No pinia (a rig, a future SSR pass): English is the catalogue's base.
    return 'en';
  }
}

/** The hub base a media element can dial (never the desktop's internal scheme). */
function audioBase(): string {
  try {
    return toDirectHubApiUrl(getRuntimeConfig().apiUrl);
  } catch {
    return '';
  }
}

/** The element, created once (hidden in the DOM so a rig can read its clock). */
function ensureAudio(): HTMLAudioElement | null {
  if (audio) return audio;
  if (typeof Audio === 'undefined' || typeof document === 'undefined') return null;

  const element = new Audio();
  element.preload = 'auto';
  element.volume = prefs.value.volume;
  // A hidden audition element: `display: none` does not stop playback, and
  // being in the document is what lets a browser rig read `currentTime`.
  element.setAttribute('data-demo-narration-audio', '');
  element.style.display = 'none';
  element.addEventListener('playing', () => {
    speaking.value = true;
    audioError.value = null;
    blockedCue = null;
  });
  element.addEventListener('pause', () => {
    speaking.value = false;
  });
  element.addEventListener('ended', () => {
    speaking.value = false;
  });
  element.addEventListener('error', () => {
    speaking.value = false;
  });
  document.body.appendChild(element);
  audio = element;
  return element;
}

/** A valid, silent WAV — the clip a Play press warms the element with. */
function silentClipUrl(): string | null {
  if (silentClip) return silentClip;
  if (typeof Blob === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return null;
  }
  const samples = 8; // 8 silent 16-bit mono samples
  const dataBytes = samples * 2;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);
  const write = (offset: number, word: string): void => {
    for (let index = 0; index < word.length; index += 1) view.setUint8(offset + index, word.charCodeAt(index));
  };
  write(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  write(8, 'WAVE');
  write(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, 8_000, true); // sample rate
  view.setUint32(28, 16_000, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  write(36, 'data');
  view.setUint32(40, dataBytes, true);
  silentClip = URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
  return silentClip;
}

/**
 * Warm the element inside a user gesture (a Play press, the test-line button).
 *
 * Chrome allows sound once the operator has interacted with the page; playing a
 * silent clip from inside the handler is what makes that permission belong to
 * THIS element rather than to a lucky following click.
 */
export function unlockDemoNarration(): void {
  const element = ensureAudio();
  audioUnlocked = true;

  // The chirp's context lives under the same policy: resume it from the gesture
  // too, so the next caption's blip is audible rather than a silent no-op.
  try {
    const Ctor =
      typeof window !== 'undefined'
        ? (window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)
        : undefined;
    if (Ctor) {
      if (!blipContext) blipContext = new Ctor();
      if (blipContext.state === 'suspended') void blipContext.resume().catch(() => {});
    }
  } catch {
    // No WebAudio in this runtime; the clips still work.
  }

  if (!element) return;
  const clip = silentClipUrl();
  if (!clip) {
    return;
  }
  const wasMuted = element.muted;
  const wasVolume = element.volume;
  try {
    element.muted = true;
    element.src = clip;
    const played = element.play();
    if (played && typeof played.then === 'function') {
      played
        .then(() => {
          element.pause();
          element.muted = wasMuted;
          element.volume = wasVolume;
        })
        .catch(() => {
          element.muted = wasMuted;
          element.volume = wasVolume;
        });
    }
  } catch {
    element.muted = wasMuted;
    element.volume = wasVolume;
  }

  // A line the browser refused before this gesture gets one retry, now that the
  // page owns audio. `speak()` clears the slot first, so a manual press never
  // double-plays through this path.
  if (blockedCue && prefs.value.enabled && retryTimer === null) {
    const cue = blockedCue;
    blockedCue = null;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      const clock = clockNow();
      if (!prefs.value.enabled || !clock.playing) return;
      stopAudio();
      playCueAudio(cue);
    }, 120);
  }
}

/** Stop whatever is speaking and rewind it (one line at a time, always). */
function stopAudio(): void {
  if (!audio) return;
  try {
    audio.pause();
    audio.currentTime = 0;
  } catch {
    // A media element with no source refuses `currentTime`; nothing to stop.
  }
  speaking.value = false;
}

/** Speak one cue through the app's single element, if it has a clip. */
function playCueAudio(cue: DemoNarrationCue): void {
  const element = ensureAudio();
  if (!element) return;
  const path = narrationAudioPath(cue, currentLanguage());
  const url = narrationAudioUrl(path, audioBase());
  if (!url) {
    // A cue whose render has not landed yet is SILENT, not an error: the caption
    // still reads, and the hub renders the clips on activation.
    audioError.value = null;
    return;
  }

  try {
    element.pause();
    element.currentTime = 0;
  } catch {
    // No source yet: the assignment below is the first one.
  }
  element.muted = false;
  element.volume = prefs.value.volume;
  // The chirp every line gets, before the voice (owner, 2026-10-22: "with
  // narration happening play a sound"). It needs no gesture beyond the one the
  // unlock already took, and it survives a clip that cannot load.
  playDemoNarrationBlip();
  element.src = url;
  try {
    const played = element.play();
    if (played && typeof played.then === 'function') {
      played
        .then(() => {
          blockedCue = null;
        })
        .catch((error: unknown) => {
          speaking.value = false;
          const name = (error as { name?: string } | null)?.name;
          // Two different failures, two different sentences: the autoplay
          // refusal is fixed by a gesture, a decode/load failure is not.
          audioError.value =
            name === 'NotAllowedError'
              ? tt(
                  'The browser blocked the narration clip — press Play once.',
                  'Brskalnik je preprečil predvajanje pripovedi — enkrat pritisnite Predvajaj.',
                )
              : tt(
                  'The narration clip could not play (hub audio missing).',
                  'Posnetka pripovedi ni bilo mogoče predvajati (manjka zvok na vozlišču).',
                );
          if (name === 'NotAllowedError') blockedCue = cue;
        });
    }
  } catch {
    speaking.value = false;
  }
}

/** The clock as the gate reads it (the app-wide frame, not a second clock). */
function clockNow(): { playing: boolean; rate: number } {
  const frame: DemoClockFrame | null = serverFrame();
  return { playing: frame?.playing === true, rate: frame?.rate ?? 0 };
}

/** Clear the hold timer (the caption has been replaced or retired). */
function clearHold(): void {
  if (holdTimer !== null) {
    clearTimeout(holdTimer);
    holdTimer = null;
  }
}

/**
 * Put a cue on the board: paint it, hold it for its own reading time, and speak
 * it when the gate allows. Everything else in this module funnels through here,
 * so a caption and its voice can never disagree about which line is current.
 */
function present(cue: DemoNarrationCue): void {
  clearHold();
  visibleCue.value = cue;

  // One element, one line: the previous clip stops the instant a new caption
  // takes the screen, whether that clip ended or not.
  stopAudio();

  const hold = holdMs(captionFor(cue, currentLanguage()).body);
  holdTimer = setTimeout(() => {
    holdTimer = null;
    promote();
  }, hold);

  const clock = clockNow();
  if (shouldSpeak({ enabled: prefs.value.enabled, playing: clock.playing, rate: clock.rate, seeking: cue.seeking })) {
    playCueAudio(cue);
  } else {
    audioError.value = null;
  }
}

/** The hold expired: the waiting cue takes the board, or the board empties. */
function promote(): void {
  const next: NarrationQueue = advanceQueue({ visible: visibleCue.value, pending: pendingCue.value });
  pendingCue.value = next.pending;
  if (next.visible) present(next.visible);
  else visibleCue.value = null;
}

/** Adopt one `demo:narration` payload (a socket event, or a rig's replay). */
export function applyDemoNarrationEvent(payload: unknown): void {
  const cue = readDemoNarrationCue(payload);
  if (!cue) return;
  lastCue = cue;

  const before: NarrationQueue = { visible: visibleCue.value, pending: pendingCue.value };
  const after = enqueueCue(before, cue);
  // The same cue again (a reconnect, a re-send): no restart, no second voice.
  if (after === before) return;

  pendingCue.value = after.pending;
  if (after.visible && after.visible !== before.visible) present(after.visible);
}

/** The demo is over, or the clock froze: nothing to say, nothing to play. */
export function resetDemoNarration(): void {
  clearHold();
  stopAudio();
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  blockedCue = null;
  visibleCue.value = null;
  pendingCue.value = null;
  lastCue = null;
  audioError.value = null;
}

/**
 * The clock went somewhere the voice may not follow: a pause, a rate past the
 * speech gate, or no demo at all. The caption stays up (its own hold retires
 * it); the audio does not.
 */
function handleFrame(frame: DemoClockFrame | null): void {
  if (!frame) {
    resetDemoNarration();
    return;
  }
  if (!frame.playing || frame.rate > NARRATION_SPEAK_MAX_RATE) stopAudio();
}

/**
 * Subscribe a hub socket to `demo:narration`. Returns the detach —
 * `stores/socket.ts` keeps it so a hub switch takes the listener, the queue and
 * the audio with the hub they belonged to.
 */
export function attachDemoNarrationListeners(socket: HubSocket): () => void {
  const onNarration = (payload: unknown): void => {
    applyDemoNarrationEvent(payload);
  };
  socket.on('demo:narration', onNarration);
  const stopFrames = onDemoFrameChange(handleFrame);

  return () => {
    socket.off('demo:narration', onNarration);
    stopFrames();
    resetDemoNarration();
  };
}

/** What a narration surface reads, and the two verbs an operator has. */
export interface DemoNarrationView {
  /** The three lines to paint, in the reader's language (`null` = nothing said). */
  caption: ComputedRef<NarrationCaption | null>;
  /** Is a cue on the board? */
  visible: ComputedRef<boolean>;
  /** The cue itself (a rig or a settings page may want its id/seeking). */
  cue: ComputedRef<DemoNarrationCue | null>;
  /** Narration switched on. */
  enabled: ComputedRef<boolean>;
  /** 0–1. */
  volume: ComputedRef<number>;
  /** The element is playing right now. */
  speaking: ComputedRef<boolean>;
  /** Why the last clip was refused (autoplay policy), or `null`. */
  error: ComputedRef<string | null>;
  /** Spoken in the reader's language (both languages ship with the cue). */
  language: ComputedRef<'en' | 'sl'>;
  setEnabled: (enabled: boolean) => void;
  setVolume: (volume: number) => void;
  /**
   * Play a cue now — the last one the hub sent when called with no argument.
   * This is the settings page's "test line": an explicit press speaks even at
   * 900× or while paused, because the operator asked for it by hand.
   */
  speak: (cue?: DemoNarrationCue | null) => void;
  /** Warm the element from inside a user gesture (a Play press). */
  unlock: () => void;
}

/** The narration board, as a surface reads it. */
export function useDemoNarration(): DemoNarrationView {
  const demo = useDemoClock();
  const locale = useLocaleStore();
  // Any surface that reads narration also arms the page-wide gesture unlock:
  // the first click anywhere (a tab, a card, the FAB) makes the next line audible.
  installDemoNarrationGestureUnlock();
  const language = computed(() => narrationLanguage(locale.currentLocale));

  const caption = computed<NarrationCaption | null>(() => {
    const cue = visibleCue.value;
    if (!cue) return null;
    return captionFor(cue, language.value, {
      dayNumber: demo.dayIndex.value + 1,
      dayWord: tt('DAY', 'DAN'),
    });
  });

  function setEnabled(enabled: boolean): void {
    writePrefs({ enabled: enabled === true, volume: prefs.value.volume });
    if (prefs.value.enabled) {
      // The switch itself is a gesture: warm the element and let the refused
      // line through, so "on" means audible immediately.
      unlockDemoNarration();
      return;
    }
    // Muting is immediate: the line stays on the board, the voice stops.
    blockedCue = null;
    stopAudio();
  }

  function setVolume(volume: number): void {
    const next = Number.isFinite(volume) ? Math.min(1, Math.max(0, volume)) : DEFAULT_PREFS.volume;
    writePrefs({ enabled: prefs.value.enabled, volume: next });
    if (audio) audio.volume = next;
  }

  function speak(cue?: DemoNarrationCue | null): void {
    const target = cue ?? lastCue;
    if (!target) return;
    if (!prefs.value.enabled) return;
    // A manual press is its own gesture: drop any pending retry so the line is
    // not spoken twice, then warm and speak.
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    blockedCue = null;
    unlockDemoNarration();
    stopAudio();
    playCueAudio(target);
  }

  return {
    caption,
    visible: computed(() => visibleCue.value !== null),
    cue: computed(() => visibleCue.value),
    enabled: computed(() => prefs.value.enabled),
    volume: computed(() => prefs.value.volume),
    speaking: computed(() => speaking.value),
    error: computed(() => audioError.value),
    language,
    setEnabled,
    setVolume,
    speak,
    unlock: unlockDemoNarration,
  };
}

/** The board's own state, for a rig — nothing in the app reads this. */
export interface DemoNarrationDebugReading {
  enabled: boolean;
  volume: number;
  speaking: boolean;
  unlocked: boolean;
  language: 'en' | 'sl';
  error: string | null;
  /** The demo frame the speech gate read (`null` = this page never synced one). */
  frame: { playing: boolean; rate: number } | null;
  cue: { id: string; dayId: string; at: string; seeking: boolean; body: string; data: string; audio: string | null } | null;
  pending: string | null;
  audio: {
    src: string;
    currentTime: number;
    duration: number;
    paused: boolean;
    volume: number;
    muted: boolean;
    readyState: number;
  } | null;
}

/** Read the narration board's raw state (the rig's `window.__demoNarration()`). */
export function demoNarrationDebug(): DemoNarrationDebugReading {
  const cue = visibleCue.value;
  const frame = serverFrame();
  return {
    enabled: prefs.value.enabled,
    volume: prefs.value.volume,
    speaking: speaking.value,
    unlocked: audioUnlocked,
    language: currentLanguage(),
    error: audioError.value,
    frame: frame ? { playing: frame.playing, rate: frame.rate } : null,
    cue: cue
      ? {
          id: cue.id,
          dayId: cue.dayId,
          at: cue.at,
          seeking: cue.seeking,
          body: captionFor(cue, currentLanguage()).body,
          data: cue.data,
          audio: narrationAudioPath(cue, currentLanguage()),
        }
      : null,
    pending: pendingCue.value ? pendingCue.value.id : null,
    audio: audio
      ? {
          src: audio.src,
          currentTime: Number.isFinite(audio.currentTime) ? audio.currentTime : 0,
          duration: Number.isFinite(audio.duration) ? audio.duration : 0,
          paused: audio.paused,
          volume: audio.volume,
          muted: audio.muted,
          readyState: audio.readyState,
        }
      : null,
  };
}

// The rigs' read door, registered once with the module (the surfaces come and
// go; the board does not). Nothing in the app reads it.
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__demoNarration = demoNarrationDebug;
}
